import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Theme provider for the Fieldlink PWA.
 * Modes: "light" | "dark" | "auto".
 * "auto" follows the OS prefers-color-scheme.
 * Applies the `dark` class to <html> so Tailwind's dark: variants kick in.
 *
 * Scope: dark mode applies inside the app and on /login.
 * Public marketing pages (/, /pricing, /faq, /register …)
 * always render in light theme so the brand site stays consistent regardless
 * of the user's logged-in preference.
 */
const STORAGE_KEY = "fl-theme";
const ThemeCtx = createContext({ theme: "auto", setTheme: () => {}, resolved: "light" });

const isAppRoute = () => {
  if (typeof window === "undefined") return false;
  return window.location.pathname.startsWith("/app") || window.location.pathname === "/login";
};

const resolve = (t) => {
  if (!isAppRoute()) return "light"; // public pages always light
  if (t === "auto") {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  }
  return t === "dark" ? "dark" : "light";
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "auto";
    try {
      return localStorage.getItem(STORAGE_KEY) || "auto";
    } catch {
      return "auto";
    }
  });
  const [resolved, setResolved] = useState(() => resolve(theme));

  // Apply resolved theme to <html>.
  useEffect(() => {
    const r = resolve(theme);
    setResolved(r);
    const root = document.documentElement;
    if (r === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* localStorage may be disabled (Safari private mode) — ignore */
    }
  }, [theme]);

  // Re-evaluate when the URL changes so navigating from /app → / strips the
  // dark class even though the user's saved preference is still "dark".
  useEffect(() => {
    const recompute = () => {
      const r = resolve(theme);
      setResolved(r);
      const root = document.documentElement;
      if (r === "dark") root.classList.add("dark");
      else root.classList.remove("dark");
    };
    window.addEventListener("popstate", recompute);
    // react-router-dom uses pushState/replaceState — patch them to fire events.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      recompute();
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args);
      recompute();
    };
    return () => {
      window.removeEventListener("popstate", recompute);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [theme]);

  // When in auto mode, react to OS theme changes live.
  useEffect(() => {
    if (theme !== "auto" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r = resolve("auto");
      setResolved(r);
      const root = document.documentElement;
      if (r === "dark") root.classList.add("dark");
      else root.classList.remove("dark");
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const set = useCallback((t) => setTheme(t), []);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme: set, resolved }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
